// Career.jsx
import React, { useRef, useState } from "react";
import { Link } from "react-router-dom";
import emailjs from "@emailjs/browser";

/**
 * Careers — Apply for jobs
 * - Responsive, accessible Bootstrap layout
 * - Sends application to career@techventurellc.com
 * - EmailJS (with attachment) OR mailto fallback if not configured
 */

// TODO: Replace with your EmailJS keys (or use env vars)
const EMAILJS = {
  SERVICE_ID: "YOUR_SERVICE_ID",
  TEMPLATE_ID: "YOUR_TEMPLATE_ID",
  PUBLIC_KEY: "YOUR_PUBLIC_KEY",
};
const EMAIL_ENABLED =
  EMAILJS.SERVICE_ID &&
  EMAILJS.TEMPLATE_ID &&
  EMAILJS.PUBLIC_KEY &&
  !/YOUR_/.test(
    `${EMAILJS.SERVICE_ID}${EMAILJS.TEMPLATE_ID}${EMAILJS.PUBLIC_KEY}`
  );

const CAREER_EMAIL = "career@techventurellc.com";

const MAX_FILE_MB = 5;
const ACCEPTED_TYPES = [
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
];

export default function Career() {
  const formRef = useRef(null);
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState({ type: "", msg: "" });
  const [fileError, setFileError] = useState("");
  const [agreed, setAgreed] = useState(false);

  const onFileChange = (e) => {
    setFileError("");
    const f = e.target.files?.[0];
    if (!f) return;
    const tooBig = f.size > MAX_FILE_MB * 1024 * 1024;
    const badType = !ACCEPTED_TYPES.includes(f.type) && !/\.docx?$/i.test(f.name) && !/\.pdf$/i.test(f.name);
    if (tooBig) setFileError(`File is too large. Max ${MAX_FILE_MB} MB.`);
    else if (badType) setFileError("Only PDF, DOC, or DOCX resumes are accepted.");
  };

  const buildMailtoFallback = (data) => {
    const lines = [
      `Position: ${data.role || "-"}`,
      `Full name: ${data.full_name || "-"}`,
      `Email: ${data.email || "-"}`,
      `Phone: ${data.phone || "-"}`,
      `Experience (years): ${data.experience || "-"}`,
      `Location: ${data.location || "-"}`,
      `LinkedIn: ${data.linkedin || "-"}`,
      `GitHub/Portfolio: ${data.github || data.portfolio || "-"}`,
      `Work authorization: ${data.work_auth || "-"}`,
      `Availability: ${data.availability || "-"}`,
      `Expected salary: ${data.salary || "-"}`,
      "",
      "Cover letter:",
      `${data.cover_letter || "-"}`,
      "",
      "— submitted via https://techventurellc.com/career",
    ].join("\n");

    const subject = encodeURIComponent(
      `Job Application — ${data.role || "Open Role"} — ${data.full_name || ""}`
    );
    const body = encodeURIComponent(lines);

    return `mailto:${CAREER_EMAIL}?subject=${subject}&body=${body}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus({ type: "", msg: "" });

    // Basic client validations
    if (fileError) {
      setStatus({ type: "danger", msg: fileError });
      return;
    }
    if (!agreed) {
      setStatus({
        type: "warning",
        msg: "Please acknowledge the privacy notice before applying.",
      });
      return;
    }

    const formEl = formRef.current;
    const formData = new FormData(formEl);
    setSubmitting(true);

    try {
      if (EMAIL_ENABLED) {
        // EmailJS can send the form + file attachment directly
        await emailjs.sendForm(
          EMAILJS.SERVICE_ID,
          EMAILJS.TEMPLATE_ID,
          formEl,
          EMAILJS.PUBLIC_KEY
        );
        setStatus({
          type: "success",
          msg:
            "Thanks! Your application has been sent. An associate will review your resume and reach out if you’re selected.",
        });
        formEl.reset();
        setAgreed(false);
      } else {
        // Fallback: open a mailto draft (user must attach resume manually)
        const data = Object.fromEntries(formData.entries());
        const href = buildMailtoFallback(data);
        window.location.href = href;
        setStatus({
          type: "info",
          msg:
            "Email client opened. Please attach your resume before sending. (Tip: configure EmailJS to enable direct sending from the page.)",
        });
      }
    } catch (err) {
      console.error(err);
      setStatus({
        type: "danger",
        msg:
          "Sorry, something went wrong while sending your application. Please try again or email us directly at career@techventurellc.com.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="career-page">
      {/* Banner / Hero */}
      <section
        className="text-white py-5"
        style={{
          background:
            "radial-gradient(1200px 600px at 10% -20%, #6d28d9 0, rgba(109,40,217,.2) 50%, transparent 60%), linear-gradient(135deg,#0ea5e9,#6d28d9)",
        }}
      >
        <div className="container">
          <div className="row justify-content-center text-center">
            <div className="col-12 col-lg-10">
              <span className="badge bg-white text-dark mb-3">TechVenture LLC</span>
              <h1 className="display-6 fw-bold mb-2">Careers at TechVenture</h1>
              <p className="lead mb-0 opacity-90">
                We’re always looking for builders. Apply below — an associate will review your
                resume and reach out if you’re selected.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Main */}
      <section className="py-4 py-md-5">
        <div className="container">
          <div className="row g-4">
            {/* Left: Form */}
            <div className="col-12 col-lg-8">
              <div className="card shadow-sm border-0">
                <div className="card-body">
                  <div className="d-flex justify-content-between align-items-start flex-wrap gap-2 mb-2">
                    <div>
                      <h2 className="h4 mb-1">Submit your application</h2>
                      <div className="text-muted small">
                        Fields marked <span className="text-danger">*</span> are required
                      </div>
                    </div>
                    <div className="text-end small">
                      <div>
                        Deliver to: <strong>{CAREER_EMAIL}</strong>
                      </div>
                      {/* <div className={`badge ${EMAIL_ENABLED ? "bg-success" : "bg-secondary"}`}>
                        {EMAIL_ENABLED ? "EmailJS enabled" : "EmailJS not configured"}
                      </div> */}
                    </div>
                  </div>

                  {status.msg ? (
                    <div className={`alert alert-${status.type} mb-3`} role="alert">
                      {status.msg}
                    </div>
                  ) : null}

                  <form ref={formRef} onSubmit={handleSubmit} noValidate>
                    <input type="hidden" name="to_email" value={CAREER_EMAIL} />
                    <div className="row g-3">
                      <div className="col-12 col-md-6">
                        <label className="form-label">
                          Full name <span className="text-danger">*</span>
                        </label>
                        <input
                          name="full_name"
                          className="form-control"
                          required
                          maxLength={80}
                          placeholder="Jane Doe"
                        />
                      </div>

                      <div className="col-12 col-md-6">
                        <label className="form-label">
                          Email <span className="text-danger">*</span>
                        </label>
                        <input
                          type="email"
                          name="email"
                          className="form-control"
                          required
                          placeholder="jane@domain.com"
                        />
                      </div>

                      <div className="col-12 col-md-6">
                        <label className="form-label">Phone</label>
                        <input
                          type="tel"
                          name="phone"
                          className="form-control"
                          placeholder="+1 555-123-4567"
                        />
                      </div>

                      <div className="col-12 col-md-6">
                        <label className="form-label">
                          Role applied for <span className="text-danger">*</span>
                        </label>
                        <select name="role" className="form-select" required>
                          <option value="">Select a role…</option>
                          <option>Backend Engineer (Java)</option>
                          <option>Frontend Engineer (React)</option>
                          <option>Full-stack Engineer</option>
                          <option>GenAI Engineer</option>
                          <option>DevOps / SRE</option>
                          <option>Data Engineer</option>
                          <option>Other</option>
                        </select>
                      </div>

                      <div className="col-12 col-md-4">
                        <label className="form-label">Experience (years)</label>
                        <input
                          type="number"
                          min="0"
                          max="50"
                          step="0.5"
                          name="experience"
                          className="form-control"
                          placeholder="5"
                        />
                      </div>

                      <div className="col-12 col-md-8">
                        <label className="form-label">Location</label>
                        <input
                          name="location"
                          className="form-control"
                          placeholder="City, Country or Remote"
                          maxLength={80}
                        />
                      </div>

                      <div className="col-12 col-md-6">
                        <label className="form-label">LinkedIn</label>
                        <input
                          type="url"
                          name="linkedin"
                          className="form-control"
                          placeholder="https://www.linkedin.com/in/username"
                        />
                      </div>

                      <div className="col-12 col-md-6">
                        <label className="form-label">GitHub / Portfolio</label>
                        <input
                          type="url"
                          name="github"
                          className="form-control"
                          placeholder="https://github.com/username or portfolio link"
                        />
                      </div>

                      <div className="col-12 col-md-6">
                        <label className="form-label">Work authorization</label>
                        <select name="work_auth" className="form-select">
                          <option value="">Select…</option>
                          <option>US Citizen / PR</option>
                          <option>H-1B</option>
                          <option>F-1 / OPT</option>
                          <option>Other</option>
                        </select>
                      </div>

                      <div className="col-12 col-md-3">
                        <label className="form-label">Availability</label>
                        <input type="date" name="availability" className="form-control" />
                      </div>

                      <div className="col-12 col-md-3">
                        <label className="form-label">Expected salary</label>
                        <input name="salary" className="form-control" placeholder="USD / year" />
                      </div>

                      <div className="col-12">
                        <label className="form-label">Cover letter</label>
                        <textarea
                          name="cover_letter"
                          className="form-control"
                          rows={4}
                          placeholder="Briefly tell us why you’re a great fit…"
                        />
                      </div>

                      <div className="col-12">
                        <label className="form-label">
                          Resume (PDF, DOC, DOCX) <span className="text-danger">*</span>
                        </label>
                        <input
                          type="file"
                          name="my_file"
                          className={`form-control ${fileError ? "is-invalid" : ""}`}
                          required
                          accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                          onChange={onFileChange}
                        />
                        {fileError ? (
                          <div className="invalid-feedback">{fileError}</div>
                        ) : (
                          <div className="form-text">
                            Max {MAX_FILE_MB} MB. Your file is used only to process the application.
                          </div>
                        )}
                      </div>

                      <div className="col-12">
                        <div className="form-check">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            id="agree"
                            checked={agreed}
                            onChange={(e) => setAgreed(e.target.checked)}
                          />
                          <label className="form-check-label" htmlFor="agree">
                            I agree that TechVenture LLC may store my details for recruitment and contact me about my application.
                          </label>
                        </div>
                      </div>

                      <div className="col-12 d-flex flex-wrap gap-2">
                        <button
                          type="submit"
                          className="btn btn-primary"
                          disabled={submitting || !!fileError || !agreed}
                        >
                          {submitting ? "Submitting…" : "Submit application"}
                        </button>

                        {!EMAIL_ENABLED && (
                          <a
                            href="mailto:career@techventurellc.com"
                            className="btn btn-outline-secondary"
                            onClick={(e) => {
                              // prevent accidental navigation; the mailto draft is opened by handleSubmit
                              if (submitting) e.preventDefault();
                            }}
                          >
                            Or email us directly
                          </a>
                        )}
                      </div>
                    </div>
                  </form>
                </div>
              </div>

              {/* Small policy note */}
              <div className="text-muted small mt-3">
                <strong>What happens next?</strong> Our recruiting team reviews every application.
                If your background is a match, we’ll reach out to schedule a conversation.
              </div>
            </div>

            {/* Right: Helpful info */}
            <div className="col-12 col-lg-4">
              <div className="card shadow-sm border-0 mb-3">
                <div className="card-body">
                  <h3 className="h6 text-uppercase text-muted mb-2">Why TechVenture</h3>
                  <ul className="small m-0 ps-3">
                    <li>Impactful projects across backend, cloud, and GenAI</li>
                    <li>Support for learning & certifications</li>
                    <li>Remote-friendly, collaborative culture</li>
                  </ul>
                </div>
              </div>

              <div className="card shadow-sm border-0 mb-3">
                <div className="card-body">
                  <h3 className="h6 text-uppercase text-muted mb-2">Application tips</h3>
                  <ul className="small m-0 ps-3">
                    <li>Link to your GitHub/portfolio and notable projects</li>
                    <li>Highlight outcomes, not just responsibilities</li>
                    <li>Mention scale, latency, reliability, or cost wins</li>
                  </ul>
                </div>
              </div>

              <div className="card shadow-sm border-0">
                <div className="card-body">
                  <h3 className="h6 text-uppercase text-muted mb-2">Questions?</h3>
                  <p className="small text-muted mb-2">
                    Reach us at <a href={`mailto:${CAREER_EMAIL}`}>{CAREER_EMAIL}</a> or see our{" "}
                    <Link to="/faq">FAQ</Link>.
                  </p>
                  <p className="small text-muted mb-0">
                    By applying, you agree to our <Link to="/privacy">Privacy Policy</Link>.
                  </p>
                </div>
              </div>
            </div>
            {/* /Right */}
          </div>
        </div>
      </section>
    </div>
  );
}
